import { EventSource } from 'eventsource';
import { z } from 'zod';

export const DEBOUNCE_TIME = 2000;
export const INITIAL_RETRY_DELAY = 1000;
export const MAX_RETRY_DELAY = 30000;

export const calculateBackoffTime = (retryCount: number) => {
  const backoff = Math.min(
    INITIAL_RETRY_DELAY * Math.pow(2, retryCount),
    MAX_RETRY_DELAY,
  );

  const jitter = Math.random() * 0.3 * backoff;
  return Math.floor(backoff + jitter);
};

export interface SSEConnectionConfig<T, R> {
  url: string;
  schema: z.ZodType<T>;
  dataConverter: (data: T) => R;
}

export interface SSEConnection<_T, R> {
  // [x: string]: any; // Allow any property type
  eventSource: EventSource | null;
  isConnecting: boolean;
  connectionError: string | null;
  isLoading: boolean;
  listeners: {
    data: ((data: R) => void)[];
    errors: ((error: string | null) => void)[];
    loading: ((loading: boolean) => void)[];
  };
  lastConnectionAttempt: number;
  retryCount: number;
  retryTimeoutId: number | null;
  connectionTimerId: number | null;
}

export const createSSEConnection = <T, R = unknown>(): SSEConnection<T, R> => {
  return {
    eventSource: null,
    isConnecting: false,
    connectionError: null,
    isLoading: false,
    listeners: {
      data: [],
      errors: [],
      loading: [],
    },
    lastConnectionAttempt: 0,
    retryCount: 0,
    retryTimeoutId: null,
    connectionTimerId: null,
  };
};

export const clearRetryTimeout = <T, R>(connection: SSEConnection<T, R>) => {
  if (connection.retryTimeoutId !== null) {
    window.clearTimeout(connection.retryTimeoutId);
    connection.retryTimeoutId = null;
  }

  if (connection.connectionTimerId !== null) {
    window.clearTimeout(connection.connectionTimerId);
    connection.connectionTimerId = null;
  }
};

export const closeEventSource = <T, R>(
  connection: SSEConnection<T, R>,
  connectionName: string,
) => {
  clearRetryTimeout(connection);

  if (connection.eventSource) {
    // Abort any pending requests
    const controller = (
      connection.eventSource as unknown as { abortController: AbortController }
    ).abortController;
    if (controller) {
      controller.abort();
    }

    // Remove all event listeners
    connection.eventSource.onmessage = null;
    connection.eventSource.onerror = null;
    connection.eventSource.onopen = null;

    // Close the connection
    connection.eventSource.close();

    // Clear the reference
    connection.eventSource = null;

    // Reset connection state
    connection.isConnecting = false;
    connection.connectionError = null;
    connection.isLoading = false;

    console.log(`${connectionName} SSE connection closed`);
  }
};

export const setupEventSource = async <T, R>(
  connection: SSEConnection<T, R>,
  config: SSEConnectionConfig<T, R>,
  getToken: () => Promise<string | null>,
  connectionName: string,
) => {
  if (connection.isConnecting) return;

  const now = Date.now();

  if (now - connection.lastConnectionAttempt < DEBOUNCE_TIME) return;

  connection.lastConnectionAttempt = now;

  connection.isConnecting = true;

  try {
    connection.isLoading = true;
    connection.listeners.loading.forEach((listener) => listener(true));

    closeEventSource(connection, connectionName);

    const token = await getToken();
    if (!token) {
      throw new Error('Authentication token is required');
    }

    // Create an AbortController for this connection
    const controller = new AbortController();
    connection.eventSource = new EventSource(config.url, {
      fetch: (input, init) =>
        fetch(input, {
          ...init,
          headers: {
            ...(init?.headers ?? {}),
            Authorization: 'Bearer ' + token,
          },
          signal: controller.signal,
        }),
    });

    // Store the controller for later use
    (
      connection.eventSource as unknown as { abortController: AbortController }
    ).abortController = controller;

    connection.eventSource.onopen = () => {
      console.log(`${connectionName} SSE connection opened`);
      connection.isConnecting = false;
      connection.connectionError = null;
      connection.isLoading = false;

      connection.retryCount = 0;

      connection.listeners.loading.forEach((listener) => listener(false));
      connection.listeners.errors.forEach((listener) => listener(null));
    };

    connection.eventSource.onerror = (error) => {
      console.error(`${connectionName} SSE connection error:`, error);
      connection.isConnecting = false;

      connection.connectionError =
        connection.eventSource?.readyState === EventSource.CLOSED
          ? `${connectionName} service not available`
          : `Failed to connect to ${connectionName} service`;

      connection.isLoading = false;

      closeEventSource(connection, connectionName);

      connection.listeners.loading.forEach((listener) => listener(false));
      connection.listeners.errors.forEach((listener) =>
        listener(connection.connectionError),
      );

      connection.retryCount++;
      const backoffTime = calculateBackoffTime(connection.retryCount);
      console.log(
        `Retrying ${connectionName} connection in ${backoffTime}ms (attempt ${connection.retryCount})`,
      );

      connection.retryTimeoutId = window.setTimeout(() => {
        setupEventSource(connection, config, getToken, connectionName);
      }, backoffTime);
    };

    connection.eventSource.addEventListener('message', (event) => {
      try {
        const data = JSON.parse(event.data);

        const result = config.schema.safeParse(data);

        if (result.success) {
          const convertedData = config.dataConverter(result.data);
          connection.listeners.data.forEach((listener) =>
            listener(convertedData),
          );
        } else {
          console.error(`Invalid ${connectionName} data:`, result.error);
        }
      } catch (error) {
        console.error(`Error processing ${connectionName}:`, error);
      }
    });

    // Set a fixed connection lifetime shorter than container lifetime
    const MAX_CONNECTION_LIFETIME = 110000; // 110 seconds

    // Proactively refresh connection before AWS App Runner container is terminated
    connection.connectionTimerId = window.setTimeout(() => {
      console.log(
        `Proactively refreshing ${connectionName} connection before container termination`,
      );
      closeEventSource(connection, connectionName);
      setupEventSource(connection, config, getToken, connectionName);
    }, MAX_CONNECTION_LIFETIME);
  } catch (error) {
    console.error(`Failed to connect to ${connectionName}:`, error);
    connection.isConnecting = false;
    connection.connectionError = `Failed to connect to ${connectionName} service`;
    connection.isLoading = false;

    connection.listeners.loading.forEach((listener) => listener(false));
    connection.listeners.errors.forEach((listener) =>
      listener(connection.connectionError),
    );

    connection.retryCount++;
    const backoffTime = calculateBackoffTime(connection.retryCount);
    console.log(
      `Retrying ${connectionName} connection in ${backoffTime}ms (attempt ${connection.retryCount})`,
    );

    connection.retryTimeoutId = window.setTimeout(() => {
      setupEventSource(connection, config, getToken, connectionName);
    }, backoffTime);
  }
};

export const subscribe = <T, R>(
  connection: SSEConnection<T, R>,
  config: SSEConnectionConfig<T, R>,
  onData: (data: R) => void,
  onError: (error: string | null) => void,
  onLoading: (loading: boolean) => void,
  getToken: () => Promise<string | null>,
  connectionName: string,
) => {
  connection.listeners.data.push(onData);
  connection.listeners.errors.push(onError);
  connection.listeners.loading.push(onLoading);

  onError(connection.connectionError);
  onLoading(connection.isLoading);

  if (!connection.eventSource && !connection.isConnecting) {
    connection.retryCount = 0;

    setupEventSource(connection, config, getToken, connectionName);
  }

  return () => {
    connection.listeners.data = connection.listeners.data.filter(
      (listener) => listener !== onData,
    );
    connection.listeners.errors = connection.listeners.errors.filter(
      (listener) => listener !== onError,
    );
    connection.listeners.loading = connection.listeners.loading.filter(
      (listener) => listener !== onLoading,
    );

    if (connection.listeners.data.length === 0 && connection.eventSource) {
      closeEventSource(connection, connectionName);
    }
  };
};

export const refresh = <T, R>(
  connection: SSEConnection<T, R>,
  config: SSEConnectionConfig<T, R>,
  getToken: () => Promise<string | null>,
  connectionName: string,
) => {
  closeEventSource(connection, connectionName);

  connection.retryCount = 0;

  setupEventSource(connection, config, getToken, connectionName);
};

/**
 * Connection Manager to handle multiple SSE connections
 */
export interface ConnectionManagerConfig {
  getToken: () => Promise<string | null>;
}

export class ConnectionManager<T = unknown, R = unknown> {
  private connections: Map<string, SSEConnection<T, R>> = new Map();
  private configs: Map<string, SSEConnectionConfig<T, R>> = new Map();
  private getToken: () => Promise<string | null>;

  constructor(config: ConnectionManagerConfig) {
    this.getToken = config.getToken;
  }

  registerConnection(
    connectionName: string,
    config: SSEConnectionConfig<T, R>,
  ): void {
    if (!this.connections.has(connectionName)) {
      this.connections.set(connectionName, createSSEConnection<T, R>());
      this.configs.set(connectionName, config);
    }
  }

  getConnection(connectionName: string): SSEConnection<T, R> | null {
    return (
      (this.connections.get(connectionName) as SSEConnection<T, R>) || null
    );
  }

  connect(
    connectionName: string,
    onData: (data: R) => void,
    onError: (error: string | null) => void,
    onLoading: (loading: boolean) => void,
  ): () => void {
    const connection = this.getConnection(connectionName);
    const config = this.configs.get(connectionName) as SSEConnectionConfig<
      T,
      R
    >;

    if (!connection || !config) {
      console.error(`Connection ${connectionName} not registered`);
      return () => {
        /* Unsubscribe function for non-existent connection */
      };
    }

    return subscribe<T, R>(
      connection,
      config,
      onData,
      onError,
      onLoading,
      this.getToken,
      connectionName,
    );
  }

  disconnect(connectionName: string): void {
    const connection = this.connections.get(connectionName);

    if (connection) {
      closeEventSource(connection, connectionName);
    }
  }

  refreshConnection(connectionName: string): void {
    const connection = this.connections.get(connectionName);
    const config = this.configs.get(connectionName);

    if (connection && config) {
      refresh(connection, config, this.getToken, connectionName);
    }
  }

  disconnectAll(): void {
    this.connections.forEach((connection, name) => {
      closeEventSource(connection, name);
    });
  }

  getConnectionStatus(connectionName: string): {
    isConnected: boolean;
    isConnecting: boolean;
    isLoading: boolean;
    error: string | null;
  } {
    const connection = this.connections.get(connectionName);

    if (!connection) {
      return {
        isConnected: false,
        isConnecting: false,
        isLoading: false,
        error: 'Connection not registered',
      };
    }

    return {
      isConnected: !!connection.eventSource,
      isConnecting: connection.isConnecting,
      isLoading: connection.isLoading,
      error: connection.connectionError,
    };
  }
}
